import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, LinearProgress,
  FormGroup, FormControlLabel, Checkbox, Chip,
} from '@mui/material';
import { ensureGooglePlacesLoaded } from '../../utils/googlePlacesLoader';
import apiService from '../../services/api';

interface ServiceMapItem {
  service_id: string;
  service_type: string;
  account_id: string;
  account_name: string | null;
  address_formatted: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_suburb: string | null;
  address_state: string | null;
  address_postcode: string | null;
  session_status: string | null;
  auth_type: string | null;
  assigned_ip: string | null;
  cpe_mac_address: string | null;
  nbn_technology_type: string | null;
}

// Colour per service type
const TYPE_COLOR: Record<string, string> = {
  nbn:           '#2563eb',
  supa_networks: '#7c3aed',
  voice:         '#0891b2',
  voice_pbx:     '#0e7490',
  data:          '#0f766e',
};
const TYPE_LABEL: Record<string, string> = {
  nbn:           'NBN',
  supa_networks: 'Supa Networks',
  voice:         'Voice',
  voice_pbx:     'Voice PBX',
  data:          'Data',
};

// Supa session status overlay colours (shown in InfoWindow; not used for pin colour)
const SESSION_STATUS_COLOR: Record<string, string> = {
  ONLINE: '#16a34a',
  STALE:  '#d97706',
  OFFLINE:'#dc2626',
};

const UNIT_PREFIX_RE = /^(unit|apt|apartment|suite|level|lot|shop|villa)\s+(.+)$/i;

function buildStreetLine(line1: string | null, line2: string | null): string {
  if (!line1) return line2 || '';
  if (!line2) return line1;
  const m = line1.match(UNIT_PREFIX_RE);
  if (m) return `${m[1]} ${m[2]}/${line2}`;
  return `${line1}/${line2}`;
}

function resolveAddress(svc: ServiceMapItem): string | null {
  if (svc.address_formatted) return svc.address_formatted;
  const streetLine = buildStreetLine(svc.address_line1, svc.address_line2);
  const parts = [streetLine, svc.address_suburb, svc.address_state, svc.address_postcode].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') + ', Australia' : null;
}

const ServicesMapPage: React.FC = () => {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allServices, setAllServices] = useState<ServiceMapItem[]>([]);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [geocodedCount, setGeocodedCount] = useState(0);
  const [geocodeErrors, setGeocodeErrors] = useState(0);
  const [totalToGeocode, setTotalToGeocode] = useState(0);
  const [loadingData, setLoadingData] = useState(true);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  // Load available types + Google Maps on mount
  useEffect(() => {
    Promise.all([
      apiService.getMappableServiceTypes(),
      ensureGooglePlacesLoaded(),
    ]).then(([types, mapsOk]) => {
      setAvailableTypes(types);
      setSelectedTypes(new Set(types));
      if (!mapsOk) {
        setError('Google Maps failed to load.');
        setLoadingData(false);
        return;
      }
      setMapsReady(true);
      setLoadingData(false);
    }).catch((err: any) => {
      setError(err.message || 'Failed to initialise map');
      setLoadingData(false);
    });
  }, []);

  // Initialise map once Maps API is ready and div is in DOM
  useEffect(() => {
    if (!mapsReady || !mapDivRef.current || mapRef.current) return;
    const g = (window as any).google;
    if (!g?.maps) return;
    mapRef.current = new g.maps.Map(mapDivRef.current, {
      center: { lat: -33.86, lng: 151.21 },
      zoom: 11,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });
  }, [mapsReady]);

  const clearMarkers = () => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
  };

  const plotServices = useCallback((services: ServiceMapItem[]) => {
    if (!mapRef.current) return;
    clearMarkers();

    const g = (window as any).google;
    const withAddress = services.map((s) => ({ svc: s, addr: resolveAddress(s) })).filter((x) => x.addr);
    const noAddr = services.length - withAddress.length;

    setTotalToGeocode(withAddress.length);
    setGeocodedCount(0);
    setGeocodeErrors(0);

    if (withAddress.length === 0) return;

    const geocoder = new g.maps.Geocoder();
    let done = 0;
    let errors = 0;
    const bounds = new g.maps.LatLngBounds();
    let pinCount = 0;

    withAddress.forEach(({ svc, addr }) => {
      geocoder.geocode({ address: addr }, (results: any[], status: string) => {
        done++;
        if (status !== 'OK' || !results?.[0]) {
          errors++;
          console.warn('[ServicesMap] geocode failed:', status, addr);
          setGeocodeErrors(errors);
          setGeocodedCount(done);
          return;
        }

        const pos = results[0].geometry.location;
        const color = TYPE_COLOR[svc.service_type] ?? '#6b7280';

        const marker = new g.maps.Marker({
          position: pos,
          map: mapRef.current,
          title: svc.account_name || svc.service_id,
          icon: {
            path: g.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: color,
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
        });

        const sessionBadge = svc.session_status
          ? `<span style="display:inline-block;padding:1px 6px;border-radius:9px;font-size:11px;font-weight:600;color:#fff;background:${SESSION_STATUS_COLOR[svc.session_status] ?? '#9ca3af'}">${svc.session_status}</span>`
          : '';

        const rows = [
          `<strong style="font-size:14px">${svc.account_name || '—'}</strong>`,
          `<span style="color:#6b7280;font-size:12px">${TYPE_LABEL[svc.service_type] ?? svc.service_type} · ${svc.service_id}</span>`,
          svc.nbn_technology_type ? `<span style="color:#6b7280;font-size:12px">${svc.nbn_technology_type}</span>` : '',
          `<hr style="margin:6px 0;border:none;border-top:1px solid #e5e7eb"/>`,
          `<span style="font-size:12px">${addr}</span>`,
          svc.session_status ? `<div style="margin-top:4px">Status: ${sessionBadge}</div>` : '',
          svc.auth_type ? `<div style="font-size:12px"><strong>Auth:</strong> ${svc.auth_type}</div>` : '',
          svc.assigned_ip ? `<div style="font-size:12px"><strong>IP:</strong> ${svc.assigned_ip}</div>` : '',
          svc.cpe_mac_address ? `<div style="font-size:12px"><strong>MAC:</strong> ${svc.cpe_mac_address}</div>` : '',
        ].filter(Boolean).join('');

        const infoWindow = new g.maps.InfoWindow({
          content: `
            <div style="font-family:sans-serif;min-width:210px;line-height:1.6">
              ${rows}
              <div style="margin-top:8px">
                <a href="/accounts/${svc.account_id}" style="color:#2563eb;font-size:12px">View account →</a>
              </div>
            </div>
          `,
        });

        marker.addListener('click', () => {
          infoWindow.open(mapRef.current, marker);
        });

        markersRef.current.push(marker);
        bounds.extend(pos);
        pinCount++;
        setGeocodedCount(done);

        if (done === withAddress.length && pinCount > 0) {
          mapRef.current.fitBounds(bounds);
        }
      });
    });

    if (noAddr > 0) console.info(`[ServicesMap] ${noAddr} service(s) have no address and were skipped`);
  }, []);

  // Fetch + plot when selected types change
  useEffect(() => {
    if (!mapsReady || selectedTypes.size === 0) {
      clearMarkers();
      setAllServices([]);
      return;
    }
    setLoadingData(true);
    apiService.getServicesMap(Array.from(selectedTypes)).then((data) => {
      setAllServices(data);
      setLoadingData(false);
      plotServices(data);
    }).catch((err: any) => {
      setError(err.message || 'Failed to load services');
      setLoadingData(false);
    });
  }, [mapsReady, selectedTypes, plotServices]);

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const geocoding = totalToGeocode > 0 && geocodedCount < totalToGeocode;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>Services Map</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Active services plotted by account address. Click a pin for details.
      </Typography>

      {/* Service type filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', display: 'block', mb: 1 }}>
          Service Types
        </Typography>
        <FormGroup row sx={{ gap: 0.5 }}>
          {availableTypes.map((type) => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  size="small"
                  checked={selectedTypes.has(type)}
                  onChange={() => toggleType(type)}
                  sx={{ color: TYPE_COLOR[type], '&.Mui-checked': { color: TYPE_COLOR[type] } }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TYPE_COLOR[type] ?? '#6b7280' }} />
                  <Typography variant="body2">{TYPE_LABEL[type] ?? type}</Typography>
                </Box>
              }
              sx={{ mr: 2 }}
            />
          ))}
        </FormGroup>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {geocodeErrors > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {geocodeErrors} address{geocodeErrors !== 1 ? 'es' : ''} could not be geocoded — check browser console for details.
        </Alert>
      )}

      <Paper sx={{ overflow: 'hidden', position: 'relative' }}>
        {(loadingData && !mapRef.current) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 620 }}>
            <CircularProgress />
          </Box>
        )}

        {geocoding && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
            <LinearProgress variant="determinate" value={Math.round((geocodedCount / totalToGeocode) * 100)} sx={{ height: 3 }} />
          </Box>
        )}

        <Box ref={mapDivRef} sx={{ height: 620, width: '100%', display: mapsReady ? 'block' : 'none' }} />
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {allServices.length} service{allServices.length !== 1 ? 's' : ''} · {totalToGeocode - geocodeErrors} plotted
          {geocodeErrors > 0 ? ` · ${geocodeErrors} failed` : ''}
        </Typography>
        {geocoding && (
          <Typography variant="caption" color="text.secondary">Geocoding…</Typography>
        )}
      </Box>
    </Box>
  );
};

export default ServicesMapPage;
